import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { PlanService } from '../services/plan.service';
import { PermissionService } from '../services/permission.service';

/**
 * Permission Guard
 * Protects routes based on TWO-TIER permissions:
 * 1. Plan/Subscription Level (FIRST): What features are available to the tenant
 * 2. RBAC Level (SECOND): What the user can do within those features
 * 
 * Usage in routes:
 * {
 *   path: 'users',
 *   canActivate: [authGuard, permissionGuard],
 *   data: { permissions: ['users:read'] }
 * }
 */
export const permissionGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
  const auth = inject(AuthService);
  const plan = inject(PlanService);
  const permission = inject(PermissionService);
  const router = inject(Router);
  const requiredPermissions = (route.data?.['permissions'] as string[]) || [];
  
  // If no permissions required, allow access
  if (requiredPermissions.length === 0) {
    return true;
  }
  
  // Check if user is authenticated
  if (!auth.isAuthenticated()) {
    router.navigate(['/auth/login']);
    return false;
  }
  
  // TWO-TIER PERMISSION CHECK:
  // Check if user has at least ONE of the required permissions (both plan and RBAC)
  const hasPermission = requiredPermissions.some(perm => permission.hasPermission(perm));
  
  if (!hasPermission) {
    // Redirect to dashboard or show access denied
    router.navigate(['/']);
    return false;
  }
  
  return true;
};

